'use client';

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Transition, TransitionStatus } from 'react-transition-group';
import { useUnit } from 'effector-react';
import { useShowWebnotesFileInfo } from '@nx-frontend/features/file-manager';
import { $isShownContextMenu } from '@nx-frontend/shared/ui/context-menu';
import { getWorkspaceImage } from '@nx-frontend/shared/ui/workspace-image';
import { generalEnv } from '@nx-frontend/shared/env';
import { useClickOutside, useDebounce } from '@nx-frontend/shared/hooks';
import { useRouter } from '@nx-frontend/shared/hooks/router-proxy';
import { useGetPagePortalMenuItem } from '@nx-frontend/shared/hooks/use-get-page-portal-menu-item';
import { useGetWebnotesFile } from '@nx-frontend/shared/hooks/use-get-webnotes-file';
import { ExpandAllParentFoldersEventParams, useSelectMenuItem } from '@nx-frontend/shared/hooks/use-select-menu-item';
import { $app } from '@nx-frontend/shared/store/app';
import { $currentWorkspace } from '@nx-frontend/shared/store/current-workspace';
import { $members } from '@nx-frontend/shared/store/org-members';
import { $orgPortalSettings } from '@nx-frontend/shared/store/org-portal-settings';
import { $portalSettings } from '@nx-frontend/shared/store/portal';
import { $sessionId } from '@nx-frontend/shared/store/portal-app';
import { $sidebar } from '@nx-frontend/shared/store/sidebar';
import { $unleashFlags } from '@nx-frontend/shared/store/unleash-flags';
import { $user } from '@nx-frontend/shared/store/user';
import { $usersState } from '@nx-frontend/shared/store/users-store';
import { $webEditor } from '@nx-frontend/shared/store/web-editor-store';
import { $workspacesAvatars } from '@nx-frontend/shared/store/workspace-avatars';
import { $workspaceMembers, fetchWorkspaceMembersEvent } from '@nx-frontend/shared/store/workspace-members-webnotes';
import { $workspaces } from '@nx-frontend/shared/store/workspaces';
import { NotificationsPortalContext } from '@nx-frontend/shared/types';
import { UnleashFeatures } from '@nx-frontend/shared/types';
import { NotificationPanel } from './notification-panel';
import {
  $activitiesStore,
  fetchImportantActivitiesFx,
  loadNotificationPanelFilterFromLocalStorageFx,
  setNotificationPanelVisibilityEvent,
} from './store';

const transitionClasses: Record<string, string> = {
  entering: 'opacity-1 transform translate-x-0',
  entered: 'opacity-1 transform translate-x-0',
  exiting: 'opacity-0 transform translate-x-0',
  exited: 'opacity-0 transform translate-x-[-100px]',
};

interface NotificationPanelWebnotesProps {
  expandAllPortalParentFolders?: (params: ExpandAllParentFoldersEventParams) => void;
}

export function NotificationPanelWrapper({ expandAllPortalParentFolders }: NotificationPanelWebnotesProps) {
  const [
    app,
    user,
    currentWorkspace,
    workspaces,
    isShownContextMenu,
    orgPortalSettings,
    activitiesStore,
    webEditor,
    webnotesSidebar,
    unleashFlags,
    portalSettings,
    usersState,
    workspacesAvatars,
    workspaceMembers,
    members,
    portalSessionId,
  ] = useUnit([
    $app,
    $user,
    $currentWorkspace,
    $workspaces,
    $isShownContextMenu,
    $orgPortalSettings,
    $activitiesStore,
    $webEditor,
    $sidebar,
    $unleashFlags,
    $portalSettings,
    $usersState,
    $workspacesAvatars,
    $workspaceMembers,
    $members,
    $sessionId,
  ]);

  const router = useRouter();

  const [
    setNotificationPanelVisibility,
    fetchImportantActivities,
    loadNotificationPanelFilterFromLocalStorage,
    fetchWorkspaceMembers,
  ] = useUnit([
    setNotificationPanelVisibilityEvent,
    fetchImportantActivitiesFx,
    loadNotificationPanelFilterFromLocalStorageFx,
    fetchWorkspaceMembersEvent,
  ]);

  const openCommentsByThreadId = useMemo(() => {
    return webEditor?.plugins?.commentManager?.options.openCommentsByThreadId;
  }, [webEditor]);

  const nodeRef = useRef(null);
  const selectMenuItem = useSelectMenuItem({ expandAllPortalParentFolders });
  const getPagePortalMenuItem = useGetPagePortalMenuItem({
    portalDomain: portalSettings.domain,
    portalId: portalSettings.id,
  });

  const routerPush = useCallback(
    (url: string) => {
      router.push(url);
    },
    [router]
  );

  useClickOutside({
    enabled: true,
    elementRef: nodeRef,
    onClickOutside: (event: MouseEvent) => {
      if (!isShownContextMenu) {
        const notificationButton = document.querySelector('button[data-cy="sidebar-notification-button"]');
        if (notificationButton && notificationButton.contains(event.target as Node)) {
          return;
        }
        setNotificationPanelVisibility(false);
      }
    },
  });

  const portalContext: NotificationsPortalContext = useMemo(() => {
    const { settings } =
      orgPortalSettings.find(({ settings }) => settings.workspaceId === currentWorkspace.globalId) || {};
    return {
      portalId: settings?.portalId ?? '',
      clientIds: [],
      role: '',
      settings: {
        name: settings?.name ?? '',
        domain: settings?.domain ?? '',
      },
      mode: 'webnotes',
      isPortalAccessDenied: portalSettings.isPortalAccessDenied,
    };
  }, [portalSettings, orgPortalSettings, currentWorkspace]);

  const debouncedFetchImportantActivities = useDebounce(fetchImportantActivities, 100);

  useEffect(() => {
    debouncedFetchImportantActivities({
      workspaceId: currentWorkspace.globalId,
      sessionId: app.sessionId,
      hostUrl: generalEnv.gatewayUrl,
      userId: user.id,
      fetchChatActivities: unleashFlags[UnleashFeatures.CHAT_NOTIFICATIONS],
      portalContext,
    });
  }, [
    debouncedFetchImportantActivities,
    currentWorkspace.globalId,
    app.sessionId,
    user.id,
    unleashFlags,
    portalContext,
  ]);

  const showWebnotesFileInfo = useShowWebnotesFileInfo();
  const getWebnotesFile = useGetWebnotesFile();

  const showFileInDashboard = useCallback(
    (globalId: string) => {
      const file = getWebnotesFile(globalId);
      if (file) {
        showWebnotesFileInfo({ file });
      }
    },
    [getWebnotesFile, showWebnotesFileInfo]
  );

  const notificationPanelWorkspace = useMemo(() => {
    const avatar = workspacesAvatars.find((avatar) => avatar.workspaceId === currentWorkspace.globalId);
    return {
      name: currentWorkspace.title,
      imageUrl: getWorkspaceImage({
        ...currentWorkspace,
        avatarStoredFileUUID: avatar?.storedFileUUID ?? '',
      }),
    };
  }, [currentWorkspace, workspacesAvatars]);

  useEffect(() => {
    fetchWorkspaceMembers(currentWorkspace.globalId);
    loadNotificationPanelFilterFromLocalStorage(currentWorkspace.globalId);
  }, [currentWorkspace.globalId, loadNotificationPanelFilterFromLocalStorage, fetchWorkspaceMembers]);

  const filterMemberList = useMemo(() => {
    const workspaces = workspaceMembers.entities ?? {};
    const workspace = workspaces[currentWorkspace.globalId] ?? [];

    let users = Object.values(usersState.entities ?? {}).filter((user) =>
      workspace.some((member) => member.userId === user.id)
    );

    // user without access to portal should not see clients in members list
    if (portalContext.isPortalAccessDenied) {
      users = users.filter((user) => {
        return !members.find((member) => member.role === 'client' && member.userId === user.id);
      });
    }
    return users;
  }, [usersState.entities, workspaceMembers, currentWorkspace.globalId, members, portalContext]);

  return (
    <Transition in={activitiesStore.show} nodeRef={nodeRef} timeout={600} unmountOnExit={true}>
      {(state: TransitionStatus) => (
        <div
          data-state={state}
          ref={nodeRef}
          className={`h-screen w-[480px] fixed top-0 transition-all z-20 ${transitionClasses[state]}`}
          style={{ left: webnotesSidebar.width + 45 + 'px' }}
        >
          <NotificationPanel
            sessionId={(app.sessionId || portalSessionId) ?? ''}
            currentWorkspaceId={currentWorkspace.globalId}
            workspaces={workspaces}
            workspace={notificationPanelWorkspace}
            assetsPrefixUrl={generalEnv.staticAssetPrefix}
            gatewayUrl={generalEnv.gatewayUrl}
            user={user}
            openCommentsByThreadId={openCommentsByThreadId}
            selectMenuItem={selectMenuItem}
            getPagePortalMenuItem={getPagePortalMenuItem}
            fetchChatActivities={unleashFlags[UnleashFeatures.CHAT_NOTIFICATIONS]}
            routerPush={routerPush}
            portalContext={portalContext}
            showFileInDashboard={showFileInDashboard}
            members={filterMemberList}
            showFilter={unleashFlags[UnleashFeatures.NOTIFICATION_PANEL_FILTER]}
          />
        </div>
      )}
    </Transition>
  );
}
